/**
 * Messaging API Service
 * TanStack Query hooks for secure client-attorney messaging
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Query Keys
export const messagingKeys = {
  all: ['messaging'] as const,
  conversations: () => [...messagingKeys.all, 'conversations'] as const,
  conversationsList: (filters?: ConversationFilters) =>
    [...messagingKeys.conversations(), 'list', filters] as const,
  conversation: (id: string) => [...messagingKeys.conversations(), 'detail', id] as const,
  messages: (conversationId: string) =>
    [...messagingKeys.all, 'messages', conversationId] as const,
  unreadCount: () => [...messagingKeys.all, 'unread'] as const,
};

// Types
export type ParticipantType = 'client' | 'attorney' | 'staff';

export interface Participant {
  id: string;
  name: string;
  type: ParticipantType;
  avatar?: string;
  email?: string;
}

export interface MessageAttachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderType: ParticipantType;
  content: string;
  timestamp: Date;
  read: boolean;
  attachments?: MessageAttachment[];
}

export interface Conversation {
  id: string;
  matterId?: string;
  matterName?: string;
  matterNumber?: string;
  participants: Participant[];
  lastMessage?: {
    content: string;
    timestamp: Date;
    senderId: string;
  };
  unreadCount: number;
  starred: boolean;
  archived: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ConversationFilters {
  starred?: boolean;
  archived?: boolean;
  matterId?: string;
  search?: string;
}

export interface SendMessageInput {
  conversationId: string;
  content: string;
  attachments?: File[];
}

export interface CreateConversationInput {
  matterId?: string;
  participantIds: string[];
  initialMessage?: string;
}

// Mock data
const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    matterId: 'matter-1',
    matterName: 'Property Purchase - 123 Main St',
    matterNumber: '2024-0089-REP',
    participants: [
      { id: 'client-1', name: 'You', type: 'client' },
      { id: 'atty-1', name: 'Sarah Johnson', type: 'attorney', email: 'sarah@lawity.com' },
    ],
    lastMessage: {
      content: 'The closing documents are ready for your review.',
      timestamp: new Date(Date.now() - 3600000),
      senderId: 'atty-1',
    },
    unreadCount: 2,
    starred: true,
    archived: false,
    createdAt: new Date(Date.now() - 86400000 * 30),
    updatedAt: new Date(Date.now() - 3600000),
  },
  {
    id: 'conv-2',
    matterId: 'matter-2',
    matterName: 'Estate Planning',
    matterNumber: '2024-0092-EST',
    participants: [
      { id: 'client-1', name: 'You', type: 'client' },
      { id: 'atty-1', name: 'Sarah Johnson', type: 'attorney' },
      { id: 'staff-1', name: 'Maria Garcia', type: 'staff' },
    ],
    lastMessage: {
      content: 'Thank you for sending the documents.',
      timestamp: new Date(Date.now() - 86400000),
      senderId: 'client-1',
    },
    unreadCount: 0,
    starred: false,
    archived: false,
    createdAt: new Date(Date.now() - 86400000 * 14),
    updatedAt: new Date(Date.now() - 86400000),
  },
  {
    id: 'conv-3',
    matterName: 'General Inquiries',
    participants: [
      { id: 'client-1', name: 'You', type: 'client' },
      { id: 'staff-1', name: 'Front Desk', type: 'staff' },
    ],
    lastMessage: {
      content: 'Your appointment is confirmed for Tuesday at 2 PM.',
      timestamp: new Date(Date.now() - 172800000),
      senderId: 'staff-1',
    },
    unreadCount: 0,
    starred: false,
    archived: false,
    createdAt: new Date(Date.now() - 86400000 * 60),
    updatedAt: new Date(Date.now() - 172800000),
  },
];

const mockMessages: Record<string, Message[]> = {
  'conv-1': [
    {
      id: 'msg-1',
      conversationId: 'conv-1',
      senderId: 'atty-1',
      senderName: 'Sarah Johnson',
      senderType: 'attorney',
      content: 'Good morning! I wanted to update you on the status of your property purchase.',
      timestamp: new Date(Date.now() - 86400000 * 2),
      read: true,
    },
    {
      id: 'msg-2',
      conversationId: 'conv-1',
      senderId: 'client-1',
      senderName: 'You',
      senderType: 'client',
      content: 'Thank you for the update. Is everything on track for closing?',
      timestamp: new Date(Date.now() - 86400000 * 2 + 3600000),
      read: true,
    },
    {
      id: 'msg-3',
      conversationId: 'conv-1',
      senderId: 'atty-1',
      senderName: 'Sarah Johnson',
      senderType: 'attorney',
      content: "Yes, everything is proceeding smoothly. The title search came back clean, and we're finalizing the closing documents now.",
      timestamp: new Date(Date.now() - 86400000),
      read: true,
    },
    {
      id: 'msg-4',
      conversationId: 'conv-1',
      senderId: 'atty-1',
      senderName: 'Sarah Johnson',
      senderType: 'attorney',
      content: "The closing documents are ready for your review. I've uploaded them to your document portal.",
      timestamp: new Date(Date.now() - 3600000),
      read: false,
      attachments: [
        { id: 'att-1', name: 'Closing_Disclosure.pdf', url: '#', type: 'application/pdf', size: 245000 },
        { id: 'att-2', name: 'Settlement_Statement.pdf', url: '#', type: 'application/pdf', size: 189000 },
      ],
    },
    {
      id: 'msg-5',
      conversationId: 'conv-1',
      senderId: 'atty-1',
      senderName: 'Sarah Johnson',
      senderType: 'attorney',
      content: 'Please review them at your convenience and let me know if you have any questions before the closing date.',
      timestamp: new Date(Date.now() - 3500000),
      read: false,
    },
  ],
  'conv-2': [
    {
      id: 'msg-6',
      conversationId: 'conv-2',
      senderId: 'atty-1',
      senderName: 'Sarah Johnson',
      senderType: 'attorney',
      content: 'We need to schedule a meeting to discuss your estate planning goals.',
      timestamp: new Date(Date.now() - 86400000 * 3),
      read: true,
    },
    {
      id: 'msg-7',
      conversationId: 'conv-2',
      senderId: 'client-1',
      senderName: 'You',
      senderType: 'client',
      content: 'Thank you for sending the documents.',
      timestamp: new Date(Date.now() - 86400000),
      read: true,
    },
  ],
  'conv-3': [
    {
      id: 'msg-8',
      conversationId: 'conv-3',
      senderId: 'staff-1',
      senderName: 'Front Desk',
      senderType: 'staff',
      content: 'Your appointment is confirmed for Tuesday at 2 PM.',
      timestamp: new Date(Date.now() - 172800000),
      read: true,
    },
  ],
};

// API Functions
async function fetchConversations(filters?: ConversationFilters): Promise<Conversation[]> {
  await new Promise((resolve) => setTimeout(resolve, 300));

  let filtered = [...mockConversations];

  if (filters?.starred !== undefined) {
    filtered = filtered.filter((c) => c.starred === filters.starred);
  }

  if (filters?.archived !== undefined) {
    filtered = filtered.filter((c) => c.archived === filters.archived);
  }

  if (filters?.matterId) {
    filtered = filtered.filter((c) => c.matterId === filters.matterId);
  }

  if (filters?.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(
      (c) =>
        c.matterName?.toLowerCase().includes(search) ||
        c.lastMessage?.content.toLowerCase().includes(search) ||
        c.participants.some((p) => p.name.toLowerCase().includes(search))
    );
  }

  // Sort by last message timestamp
  filtered.sort((a, b) => {
    const aTime = a.lastMessage?.timestamp.getTime() || a.updatedAt.getTime();
    const bTime = b.lastMessage?.timestamp.getTime() || b.updatedAt.getTime();
    return bTime - aTime;
  });

  return filtered;
}

async function fetchConversation(id: string): Promise<Conversation> {
  await new Promise((resolve) => setTimeout(resolve, 150));

  const conversation = mockConversations.find((c) => c.id === id);
  if (!conversation) {
    throw new Error('Conversation not found');
  }

  return conversation;
}

async function fetchMessages(conversationId: string): Promise<Message[]> {
  await new Promise((resolve) => setTimeout(resolve, 200));

  return mockMessages[conversationId] || [];
}

async function sendMessage(input: SendMessageInput): Promise<Message> {
  await new Promise((resolve) => setTimeout(resolve, 300));

  const newMessage: Message = {
    id: `msg-${Date.now()}`,
    conversationId: input.conversationId,
    senderId: 'client-1',
    senderName: 'You',
    senderType: 'client',
    content: input.content,
    timestamp: new Date(),
    read: true,
  };

  // Add to mock messages
  if (!mockMessages[input.conversationId]) {
    mockMessages[input.conversationId] = [];
  }
  mockMessages[input.conversationId].push(newMessage);

  // Update conversation's last message
  const convIdx = mockConversations.findIndex((c) => c.id === input.conversationId);
  if (convIdx !== -1) {
    mockConversations[convIdx] = {
      ...mockConversations[convIdx],
      lastMessage: {
        content: input.content,
        timestamp: new Date(),
        senderId: 'client-1',
      },
      updatedAt: new Date(),
    };
  }

  return newMessage;
}

async function markAsRead(conversationId: string): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  // Mark all messages as read
  if (mockMessages[conversationId]) {
    mockMessages[conversationId] = mockMessages[conversationId].map((m) => ({
      ...m,
      read: true,
    }));
  }

  // Reset unread count
  const convIdx = mockConversations.findIndex((c) => c.id === conversationId);
  if (convIdx !== -1) {
    mockConversations[convIdx] = {
      ...mockConversations[convIdx],
      unreadCount: 0,
    };
  }
}

async function toggleStar(conversationId: string): Promise<Conversation> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  const convIdx = mockConversations.findIndex((c) => c.id === conversationId);
  if (convIdx === -1) {
    throw new Error('Conversation not found');
  }

  mockConversations[convIdx] = {
    ...mockConversations[convIdx],
    starred: !mockConversations[convIdx].starred,
  };

  return mockConversations[convIdx];
}

async function archiveConversation(conversationId: string): Promise<Conversation> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  const convIdx = mockConversations.findIndex((c) => c.id === conversationId);
  if (convIdx === -1) {
    throw new Error('Conversation not found');
  }

  mockConversations[convIdx] = {
    ...mockConversations[convIdx],
    archived: true,
  };

  return mockConversations[convIdx];
}

async function fetchUnreadCount(): Promise<number> {
  await new Promise((resolve) => setTimeout(resolve, 100));

  return mockConversations.reduce((sum, c) => sum + c.unreadCount, 0);
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch list of conversations
 */
export function useConversations(filters?: ConversationFilters) {
  return useQuery({
    queryKey: messagingKeys.conversationsList(filters),
    queryFn: () => fetchConversations(filters),
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Fetch a single conversation by ID
 */
export function useConversation(id: string) {
  return useQuery({
    queryKey: messagingKeys.conversation(id),
    queryFn: () => fetchConversation(id),
    enabled: !!id,
  });
}

/**
 * Fetch messages for a conversation
 */
export function useMessages(conversationId: string) {
  return useQuery({
    queryKey: messagingKeys.messages(conversationId),
    queryFn: () => fetchMessages(conversationId),
    enabled: !!conversationId,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Fetch total unread count
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: messagingKeys.unreadCount(),
    queryFn: fetchUnreadCount,
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Poll every minute
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Send a message
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (newMessage) => {
      // Add message to cache
      queryClient.setQueryData<Message[]>(
        messagingKeys.messages(newMessage.conversationId),
        (old) => (old ? [...old, newMessage] : [newMessage])
      );

      // Invalidate conversations to update last message
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

/**
 * Mark conversation as read
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAsRead,
    onSuccess: (_, conversationId) => {
      // Update messages in cache
      queryClient.setQueryData<Message[]>(
        messagingKeys.messages(conversationId),
        (old) => old?.map((m) => ({ ...m, read: true })) || []
      );

      // Invalidate conversations to update unread count
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.unreadCount() });
    },
  });
}

/**
 * Toggle conversation star
 */
export function useToggleStar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: toggleStar,
    onSuccess: (updatedConversation) => {
      queryClient.setQueryData(
        messagingKeys.conversation(updatedConversation.id),
        updatedConversation
      );
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

/**
 * Archive a conversation
 */
export function useArchiveConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: archiveConversation,
    onSuccess: (updatedConversation) => {
      queryClient.setQueryData(
        messagingKeys.conversation(updatedConversation.id),
        updatedConversation
      );
      queryClient.invalidateQueries({ queryKey: messagingKeys.conversations() });
    },
  });
}

export default {
  useConversations,
  useConversation,
  useMessages,
  useUnreadCount,
  useSendMessage,
  useMarkAsRead,
  useToggleStar,
  useArchiveConversation,
  messagingKeys,
};
